import { useMutation, useQueryClient } from "@tanstack/react-query";
import { loyaltiesEventRecordsKeys } from "Api/Queries/Loyalty/useLoyaltiesEventRecordsQuery";
import { loyaltiesEventsKeys } from "Api/Queries/Loyalty/useLoyaltiesEventsQuery";
import { loyaltiesOverviewKeys } from "Api/Queries/Loyalty/useLoyaltiesOverviewQuery";
import {
  type PartyLoyaltyPointsDto,
  type ProcessLoyaltyEventCommandResult,
  type ProcessLoyaltyEventRequest,
  postLoyaltiesEventRecords,
} from "Infrastructure/Api/Api";
import {
  getApiErrorStatus,
  processResponseWithApiProblemDetails,
} from "Infrastructure/Api/ApiUtils";

const HTTP_CONFLICT_STATUS = 409;

type Props = {
  onSuccess?: (loyaltyPoints: PartyLoyaltyPointsDto) => void;
  onError?: (error: unknown, variables: ProcessLoyaltyEventRequest) => void;
};

export const useSendLoyaltyEventRecordMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();

  // Loyalty screens often load in parallel with the event POST — refresh them so the
  // awarded points and completed missions show without waiting for the cache to expire.
  const invalidateLoyaltyQueries = () => {
    queryClient.invalidateQueries({ queryKey: loyaltiesOverviewKeys.all });
    queryClient.invalidateQueries({ queryKey: loyaltiesEventsKeys.all });
    queryClient.invalidateQueries({ queryKey: loyaltiesEventRecordsKeys.all });
  };

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      (data: ProcessLoyaltyEventRequest) => postLoyaltiesEventRecords(data),
    ),
    onSuccess: (data: ProcessLoyaltyEventCommandResult) => {
      invalidateLoyaltyQueries();
      onSuccess?.(data.partyLoyaltyPointsDto);
    },
    onError: (error: unknown, variables: ProcessLoyaltyEventRequest) => {
      // Conflict = the event was already recorded elsewhere (another tab, session or device),
      // so the backend is ahead of this tab's cache just like after a success.
      if (getApiErrorStatus(error) === HTTP_CONFLICT_STATUS) {
        invalidateLoyaltyQueries();
      }
      onError?.(error, variables);
    },
  });
};
